import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  addProviderSettings,
  createServiceProvider,
  ORDER_NAMES,
  serviceProvidersShellQuery,
  serviceProvidersTableQuery,
  type NewProvider,
  type ProviderModule,
} from '../../api/serviceProviders'
import { ApiError } from '../../api/client'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './ServiceProvidersPage.css'

/**
 * Service Providers, element cu element fata de
 * `views/version-3/settings/service_provider/index.php` si coloana cu coloana
 * fata de `spModel` din `public/js/settings/service_providers.js`.
 *
 * Ecranul sta la ACEEASI adresa ca in portal - `/app/services_providers/service_providers`
 * - fiindca aia e scrisa in `Services\Navigation\SettingsMenu`, si de acolo o
 * citesc si rail-ul zonei Global, si sertarul rotitei. Nu e sub `settings/`,
 * desi ecranul e al setarilor: adresa a ramas cea a controllerului vechi.
 *
 * -----------------------------------------------------------------------------
 * BUTONUL DE ADAUGARE
 * -----------------------------------------------------------------------------
 *
 * In portal a fost multa vreme invizibil: se punea din JavaScript, cu
 * `PageButtons.newSPModal($('.dataTableA'))`, iar tabelul ecranului poarta clasa
 * `dataTableM` - selectorul nu prindea nimic. Acum sta scris langa titlu, unde
 * se si vede daca lipseste, si apare numai daca serverul spune ca omul poate
 * adauga (`canCreate`: dreptul de scriere SI `extended_rights == 2`).
 *
 * Ascunderea din ecran NU e poarta: acelasi lucru se verifica din nou la
 * fiecare din cele doua capete de scriere.
 *
 * -----------------------------------------------------------------------------
 * DOUA CERERI PENTRU O SINGURA APASARE
 * -----------------------------------------------------------------------------
 *
 * "Create" scrie furnizorul, firma si administratorul; imediat dupa, a doua
 * cerere copiaza sabloanele peste ei - servicii, tipuri si statusuri de tichet,
 * fluxuri, categorii, chat, alarme, module, licenta. Asa e si in portal
 * (`$('#new-sp-form').ajaxForm` cheama `addSettings` din `success`), si asa
 * ramane: al doilea pas e o suta de scrieri care se pot relua, iar reluarea nu
 * trebuie sa faca inca un furnizor.
 */

const TABLE_ID = 'SPlist'

/** `spModel` din service_providers.js: doua coloane, si amandoua scriu `<p>`. */
export function providerColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'ID',
      orderable: true,
      visible: true,
      width: 100,
      html: (row) => `<p>${Number(row.ID)}</p>`,
    },
    {
      index: 1,
      title: 'Service Provider Name',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<p>${esc(String(row.Nume ?? ''))}</p>`,
    },
  ]
}

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

const EMPTY: NewProvider = {
  company_name: '',
  Nume: '',
  offices: '',
  firstname: '',
  lastname: '',
  email: '',
  username: '',
  password: '',
  modules: [],
  chat_template: '',
  license_type: '',
}

export function ServiceProvidersPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(serviceProvidersShellQuery())
  /* `order: [[1, 'desc']]`, `length: 50` - cum porneste lista veche. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 50,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const [creating, setCreating] = useState(false)
  const columns = useMemo(() => providerColumns(), [])

  const table = useQuery({
    ...serviceProvidersTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'Nume',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (isForbidden(shell.error)) return <Forbidden />
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['services_providers'] })
  }

  return (
    <div className="od-sp">
      {/*
        Capul ecranului: ce e lista, si butonul care adauga. Scria "Create
        Service Provider" pe un ecran care ARATA furnizorii - titlul unei
        ferestre, pus peste o lista - si nu avea nicaieri butonul care deschide
        chiar fereastra aia.
      */}
      <div className="od-sp-head">
        <div>
          <h4>
            <strong>Service</strong> Providers
          </h4>
          <label className="color" style={{ marginBottom: 0 }}>
            The service providers of this platform
          </label>
        </div>
        {shell.data.canCreate && (
          <button
            type="button"
            className="btn btn-primary"
            title="Create new Service Provider"
            onClick={() => setCreating(true)}
          >
            <i className="fas fa-plus" aria-hidden="true"></i> Add Service Provider
          </button>
        )}
      </div>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => `row_${Number(row.ID)}`}
        emptyHint="A service provider is a company that runs OptimumDesk for its own customers."
        /* `service_providers.js` (buttons: DataTablesProperties.buttons):
           Copy/Excel/PDF, Columns si Refresh - lipseau cu totul. */
        footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
      />
      <NewProviderModal
        open={creating}
        modules={shell.data.modules}
        chatTemplates={shell.data.chatTemplates}
        licenseTypes={shell.data.licenseTypes}
        onClose={() => setCreating(false)}
        onSaved={redraw}
      />
    </div>
  )
}

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/**
 * Fereastra "New SP", camp cu camp din macheta veche.
 *
 * DOUA CASUTE CARE NU DUC NICAIERI, si raman fiindca asa e ecranul azi:
 *
 *  - "Offices". `Firma::createSp()` face sediul numai daca primeste cheia
 *    `Offices` cu litera mare, iar formularul trimite `offices`. Deci `it_sedii`
 *    nu primeste nimic, si nici `it_licente` - `setLicences()` incepe cu
 *    `if ($office)`, iar `$officeId` ramane 0.
 *  - "Username". `LoginHandle::forAccount()` intoarce emailul cand exista, si
 *    abia altfel numele de utilizator; cu emailul cerut mai jos, aceasta casuta
 *    nu ajunge niciodata in baza.
 *
 * Nu se sterg si nu se schimba aici: ar fi o hotarare despre ce face ecranul,
 * nu despre unde sta. Sunt scrise ca sa se stie.
 *
 * CELE DOUA DATE DE LICENTA din macheta veche NU s-au mutat: `addSettings`
 * trimitea numai `license_type`, deci `start_license` si `end_license` intrau
 * NULL orice s-ar fi ales in ele. Doua campuri de calendar care nu ajung
 * nicaieri sunt mai rele decat lipsa lor - omul crede ca a pus un termen.
 */
export function NewProviderModal({
  open,
  modules,
  chatTemplates,
  licenseTypes,
  onClose,
  onSaved,
}: {
  open: boolean
  modules: ProviderModule[]
  chatTemplates: Record<string, string>
  licenseTypes: Record<string, string>
  onClose(): void
  onSaved(): void
}) {
  const [form, setForm] = useState<NewProvider>(EMPTY)

  /* Furnizorul nou porneste cu modulele implicite bifate; restul raman oprite. */
  useEffect(() => {
    if (!open) return
    setForm({ ...EMPTY, modules: modules.filter((one) => one.default === true).map((one) => one.slug) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const save = useMutation({
    mutationFn: async (value: NewProvider) => {
      const created = await createServiceProvider(value)
      toast('Create new Service Provider.', 'Success')

      if (created.service_provider > 0) {
        const settings = await addProviderSettings(created, value)

        if (settings.errors.length > 0) {
          /* Erorile pasului al doilea se arata pe rand, ca in portal: fiecare e
             un sablon care n-a putut fi copiat, si furnizorul exista deja. */
          settings.errors.forEach((why) => toast(why, 'Error', 'error'))
        } else {
          toast('Add Settings.', 'Success')
        }
      }

      return created
    },
    onSuccess: () => {
      setForm(EMPTY)
      onClose()
      onSaved()
    },
    onError: (error) => toast(failed(error), 'Error', 'error'),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate(form)
  }

  const set = (patch: Partial<NewProvider>) => setForm((old) => ({ ...old, ...patch }))

  const close = () => {
    /* `$('#newSPModal').on('hidden.bs.modal', ... form.reset())`. */
    setForm(EMPTY)
    onClose()
  }

  return (
    <Modal
      id="newSPModal"
      open={open}
      onClose={close}
      labelledBy="newSPModalLabel"
      title={
        <>
          <i className="fas fa-business-time" aria-hidden="true"></i> New SP
        </>
      }
    >
      <form id="new-sp-form" onSubmit={submit}>
        <div className="od-sp-modal-body">
          <div className="form-group">
            <label htmlFor="company_name">
              Service Provider<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="company_name"
              required
              placeholder="Service Provider Name"
              value={form.company_name}
              onChange={(e) => set({ company_name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="Nume">
              Company<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="Nume"
              required
              placeholder="Company Name"
              value={form.Nume}
              onChange={(e) => set({ Nume: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="offices">
              Offices<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="offices"
              required
              placeholder="Offices"
              value={form.offices}
              onChange={(e) => set({ offices: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sp-firstname">
              First Name<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="sp-firstname"
              required
              placeholder="First Name"
              value={form.firstname}
              onChange={(e) => set({ firstname: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sp-lastname">
              Last Name<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="sp-lastname"
              required
              placeholder="Last Name"
              value={form.lastname}
              onChange={(e) => set({ lastname: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sp-email">
              Email<span className="required">*</span>
            </label>
            <input
              type="email"
              className="form-control input-sm"
              id="sp-email"
              required
              placeholder="Email"
              value={form.email}
              onChange={(e) => set({ email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sp-username">
              Username<span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="sp-username"
              required
              placeholder="Username"
              value={form.username}
              onChange={(e) => set({ username: e.target.value })}
            />
            <span className="help-block od-sp-note">
              The account signs in with the email above; this is kept only because the old form has it.
            </span>
          </div>
          <div className="form-group">
            <label htmlFor="sp-password">
              Password<span className="required">*</span>
            </label>
            <input
              type="password"
              className="form-control input-sm"
              id="sp-password"
              required
              placeholder="Password"
              value={form.password}
              onChange={(e) => set({ password: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sp-modules">Modules</label>
            {/*
              Valoarea e SLUG-ul, nu id-ul. In portal se trimiteau numere, iar
              `ModulesAccess` le cauta cu `getModuleBySlug()` - adica
              `WHERE slug = <numar>`. Nu se potrivea nimic si nu se scria nimic,
              tacut: bifele de module erau decorative.
            */}
            <select
              id="sp-modules"
              className="form-control"
              multiple
              size={8}
              required
              value={form.modules}
              onChange={(e) =>
                set({ modules: Array.from(e.target.selectedOptions, (option) => option.value) })
              }
            >
              {modules.map((module) => (
                <option key={module.id} value={module.slug}>
                  {module.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="chat_template">
              Chat Template<span className="required">*</span>
            </label>
            <select
              id="chat_template"
              className="form-control"
              required
              value={form.chat_template}
              onChange={(e) => set({ chat_template: e.target.value })}
            >
              <option value="">Choose..</option>
              {Object.entries(chatTemplates).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="license_type">
              License type:<span className="required">*</span>
            </label>
            <select
              id="license_type"
              className="form-control"
              required
              value={form.license_type}
              onChange={(e) => set({ license_type: e.target.value })}
            >
              <option value="">Choose..</option>
              {Object.entries(licenseTypes).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-default" onClick={close}>
            Close
          </button>
          <button type="submit" className="btn btn-primary submit-new-sp" disabled={save.isPending}>
            {save.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
